import { CheckCheckIcon, Undo2Icon } from "lucide-react"
import type * as React from "react"
import { useId } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import type { DraftStatus } from "@steelbill/types"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { ApiError } from "@/lib/api-error"

/**
 * "Mark as ready" / "Back to draft", beside "Save now" in all three editors
 * (§7.2). It said "beside Save draft in BOTH editors" until 2026-08-16, which
 * had gone wrong twice over: the label was renamed, and the challan editor made
 * three.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT READY MEANS, AND WHAT IT DOES NOT. It says a saved FORM has been read
 * through and is fit to issue — nothing more. It allocates no number, writes no
 * document and is reversible in one press. Issuing is the irreversible act that
 * spends a number (§6), and it is still the button at the bottom of the screen.
 * The value is on the drafts list: half a dozen unfinished forms, and this is
 * what tells a half-typed one from one somebody checked and left.
 *
 * IT DOES NOT RIDE THE AUTOSAVE. The save routes refuse a `status` key outright
 * — a status carried on a write that fires every two seconds while somebody
 * types would flip as a side effect of typing, and "it went back to draft on its
 * own" is a complaint the owner would have no way to describe.
 *
 * BEFORE THE FIRST AUTOSAVE THERE IS NOTHING TO MARK, so the button is DISABLED
 * rather than pressable-and-silent. The reason is on screen next to it either
 * way — the header shows "Not saved yet — saves by itself once you start" in
 * exactly that state — and it is attached to the button itself through
 * `aria-describedby`, because a disabled button takes no pointer events, so a
 * `title` on it is a tooltip nobody can ever summon.
 *
 * REVERSIBLE ON PURPOSE. Ready is a note between two people, not a lock: the
 * form stays fully editable, and someone who marked one early presses again.
 * A one-way flag would need an escape hatch, and the escape hatch would be this
 * button.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function DraftReadyControl({
  status,
  exists,
  pending,
  onChange,
}: {
  status: DraftStatus
  /** False until the first autosave has created the row on the server. */
  exists: boolean
  pending: boolean
  /** Rejects on failure — this is a press, and a press that appears to do
   * nothing is worse than an error. */
  onChange: (next: DraftStatus) => Promise<void>
}): React.JSX.Element {
  const { t } = useTranslation()
  const reasonId = useId()

  const ready = status === "READY"
  const next: DraftStatus = ready ? "DRAFT" : "READY"

  return (
    <>
      {/*
        * NO STATE BADGE HERE ANY MORE. There used to be a green "Ready" pill
        * beside this button, and since 2026-08-15 the draft's NAME carries the
        * state instead — the header shows `READY/INV/12` in grey, `DRAFT/INV/12`
        * in amber. Keeping both meant one state wearing two colours in two
        * places on the same screen, and the button's own label already says
        * which way the press goes.
        */}
      <Button
        type="button"
        variant="outline"
        disabled={!exists || pending}
        aria-describedby={exists ? undefined : reasonId}
        onClick={() => {
          void onChange(next)
            .then(() => {
              /*
               * The badge beside this button already changed, so the toast is
               * not there to confirm the press — it is there to say what READY
               * is NOT. Marking a form ready spends no number and files no
               * document (§6); somebody who reads the green badge as "sent"
               * would stop looking for the button that actually issues it.
               */
              toast.success(t(next === "READY" ? "draft.markedReady" : "draft.markedDraft"))
            })
            .catch((error: unknown) => {
              toast.error(
                error instanceof ApiError ? error.message : t("err.somethingWentWrong"),
              )
            })
        }}
      >
        {pending ? <Spinner data-icon="inline-start" /> : ready ? <Undo2Icon /> : <CheckCheckIcon />}
        {ready ? t("btn.backToDraft") : t("btn.markReady")}
      </Button>

      {exists ? null : (
        <span id={reasonId} className="sr-only">
          {t("draft.readyNeedsDraft")}
        </span>
      )}
    </>
  )
}
